import { useTheme } from '@automata/mui-theme';
import { CSSProperties, MouseEventHandler } from 'react';

import { Text } from '../Text';

export type BarProps = {
  text?: string;
  x?: number;
  y?: number;
  width: number;
  height: number;
  fill?: string;
  rx?: number;
  onMouseOver?: MouseEventHandler<SVGGElement>;
  onMouseOut?: MouseEventHandler<SVGGElement>;
  'data-testid'?: string;
  opacity?: CSSProperties['opacity'];
  fillOpacity?: CSSProperties['fillOpacity'];
  stroke?: CSSProperties['stroke'];
  strokeWidth?: CSSProperties['strokeWidth'];
  strokeOpacity?: CSSProperties['strokeOpacity'];
  strokeDasharray?: CSSProperties['strokeDasharray'];
  textFill?: CSSProperties['fill'];
  icon?: (fill: CSSProperties['fill']) => React.ReactNode;
  iconAriaLabel?: string;
  iconOnClick?: MouseEventHandler<HTMLButtonElement>;
};

export const Bar = ({
  text,
  x = 0,
  y = 0,
  width,
  height,
  fill,
  rx = 2,
  onMouseOver,
  onMouseOut,
  'data-testid': testId,
  opacity = 1,
  fillOpacity = 1,
  stroke = fill,
  strokeWidth = 2,
  strokeOpacity = 2,
  strokeDasharray,
  textFill,
  icon,
  iconAriaLabel,
  iconOnClick,
}: BarProps): JSX.Element => {
  const iconButtonWidth = 40;
  const iconWidth = 24;
  const padding = 8;
  const centerY = y + height / 2;
  const iconXPos = x + padding;
  const iconYPos = centerY - iconButtonWidth / 2;

  let textXPos = x + padding;
  let maxWidth = width - padding * 2;
  let shouldRenderIcon = false;

  if (icon) {
    textXPos += iconButtonWidth + padding;
    maxWidth -= iconButtonWidth + padding;
    if (
      width > iconButtonWidth + padding * 2 &&
      height > iconButtonWidth + padding * 2
    ) {
      shouldRenderIcon = true;
    }
  }

  const theme = useTheme();

  const contrastFill = theme.palette.getContrastText(fill ?? '');

  return (
    <g onMouseOver={onMouseOver} onMouseOut={onMouseOut} data-testid={testId}>
      <rect
        x={x}
        y={y}
        width={Math.max(width, 0)}
        height={Math.max(height, 0)}
        rx={rx}
        style={{ cursor: 'pointer' }}
        stroke={stroke}
        strokeWidth={strokeWidth}
        opacity={opacity}
        fillOpacity={fillOpacity}
        strokeOpacity={strokeOpacity}
        strokeDasharray={strokeDasharray}
        fill={fill}
      />
      {icon !== undefined && shouldRenderIcon && (
        <g transform={`translate(${iconXPos}, ${iconYPos})`}>
          <foreignObject
            x={0}
            y={0}
            width={iconButtonWidth}
            height={iconButtonWidth}
          >
            <button
              type="button"
              aria-label={iconAriaLabel}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                width: iconButtonWidth,
                height: iconButtonWidth,
              }}
              onClick={iconOnClick}
            >
              <svg width={iconWidth} height={iconWidth} viewBox="0 0 24 24">
                {icon(textFill ?? contrastFill)}
              </svg>
            </button>
          </foreignObject>
        </g>
      )}
      {text && (
        <Text
          fill={textFill ?? contrastFill}
          y={centerY}
          x={textXPos}
          maxWidth={Math.max(maxWidth, 0)}
          maxHeight={Math.max(height - padding, 0)}
          dominantBaseline="middle"
          alignmentBaseline="central"
          textAnchor="start"
          cursor="pointer"
          // fixme: fix MUI theme types
          fontSize={Number(theme.typography.subtitle1.fontSize)}
          letterSpacing={theme.typography.subtitle1.letterSpacing}
          fontWeight={theme.typography.subtitle1.fontWeight}
        >
          {text}
        </Text>
      )}
    </g>
  );
};
